'use client';
import React, { useEffect, useRef } from 'react';
import ProductCard from './ProductCard';
import './ProductNew.css';
import { useTrendingProducts } from '../hooks/useFirestore';
import { useCart } from '../context/CartContext';
import { soundEffects } from '../utils/soundEffects';
import { generateProductSlug, getProductPathPrefix } from '../utils/seoUtils';
import { useNavigate } from 'react-router-dom';
import { useEnhancedAddToCart } from '../hooks/useEnhancedAddToCart';

export default function ProductNew() {
  const sectionRef = useRef(null);
  const headRef = useRef(null);
  const { data: firebaseProducts, loading } = useTrendingProducts();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const { handleEnhancedAddToCart } = useEnhancedAddToCart();

  const handleProductClick = (product) => {
    // Find matching firebase product to get all data for slug
    const fbProduct = firebaseProducts.find(p => p.id === product.id);
    if (!fbProduct) return;
    
    const slug = generateProductSlug(fbProduct);
    const pathPrefix = getProductPathPrefix(fbProduct.productType);
    navigate(`/${pathPrefix}/${slug || product.id}`);
  };

  // Transform Firebase data to the format used by the grid
  const transformData = (products) => {
    return products.map(product => {
      // Map productType to collections
      let category = 'All';
      if (product.productType === 'laptop') category = 'Laptops';
      else if (product.productType === 'minipc') category = 'Mini PCs';

      // Features string (RAM + Storage)
      const features = [
        product.ramSize ? `${product.ramSize} RAM` : '8GB RAM',
        product.storageSize ? `${product.storageSize} ${product.storageType || 'SSD'}` : '256GB SSD'
      ].join(' · ');

      return {
        id: product.id,
        name: product.name || 'Premium Device',
        brand: product.brand || 'Nexawave',
        subtitle: `${product.processor || 'Latest Processor'} · ${product.screenSize || '15.6"'} Screen Size`,
        scentNote: features, // Replaced scent note with RAM/Storage
        collection: category,
        originalPrice: product.originalPrice || product.price * 1.2 || 0,
        salePrice: product.price || 0,
        badge: product.badge || 'Trending',
        rating: 4.5 + Math.random() * 0.5, // Random high rating
        reviews: Math.floor(Math.random() * 200) + 50, // Random reviews
        image: product.imageUrls?.[0] || product.image || "https://images.unsplash.com/photo-1517336714731-489689fd1ca8",
        imageAlt: product.name,
        isNew: product.isNew || false,
        stockLeft: product.stock || 15,
        features: product.features || [] // Original features for cart
      };
    });
  };

  const processedProducts = (firebaseProducts && firebaseProducts.length > 0) ? transformData(firebaseProducts) : [];

  const handleAddToCart = (product, e) => {
    // Construct normalized product for enhanced hook
    const normalizedProduct = {
      ...product,
      price: product.salePrice,
      imageUrls: [product.image]
    };

    handleEnhancedAddToCart(normalizedProduct, null, null, e?.currentTarget);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.15 }
    );
    if (headRef.current) observer.observe(headRef.current);
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);


  const splitWords = (text, startDelay = 0) => {
    const words = text.trim().split(/\s+/);
    return words.map((word, i) => (
      <span
        key={`${word}-${i}`}
        className="word-animate"
        style={{ animationDelay: `${startDelay + i * 40}ms` }}
      >
        {word}
        {i < words.length - 1 ? ' ' : ''}
      </span>
    ));
  };


  return (
    <section id="product-new" className="py-16 px-6 sm:px-10 bg-clinical-white">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div ref={headRef} className="section-fade mb-12">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-6 h-[1px] bg-teal" />
                <span className="font-mono-label text-[9px] font-medium text-teal uppercase tracking-[0.5em]">
                  Trending Products
                </span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" style={{ color: '#0D1F17' }}>
                {splitWords("Check out what's", 100)}{' '}
                <span className="text-teal inline-block">
                  {splitWords("popular right now.", 200)}
                </span>
              </h2>
            </div>
            <div className="hidden sm:block">
              <a href="/products" className="view-all-btn" style={{ textDecoration: 'none' }}>
                <span>View All</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div
          ref={sectionRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 stagger-children"
          style={{ minHeight: loading && processedProducts.length === 0 ? '400px' : 'auto' }}>
          
          {loading && processedProducts.length === 0 ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="animate-pulse bg-clinical-white/10 rounded-xl aspect-[3/4]" />
            ))
          ) : (
            processedProducts.map((product) =>
              <ProductCard 
                key={product.id} 
                product={product} 
                onAddToCart={(e) => handleAddToCart(product, e)} 
                onCardClick={() => handleProductClick(product)}
              />
            )
          )}
        </div>

        <div className="flex justify-center mt-12 sm:hidden">
          <a href="/products" className="view-all-btn" style={{ textDecoration: 'none' }}>
            <span>View All Products</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </div>
    </section>);

}
